import { List, Tag, Button, Space, Typography, Empty, Alert, Popconfirm, App } from 'antd'
import { DeleteOutlined, PlusOutlined, CloseOutlined } from '@ant-design/icons'
import { useState, useEffect } from 'react'
import { getLoginInternalAccess, deleteAccess } from '../services/internal-access-api'
import { formatRole, type ZealysLogin, type LoginInternalAccess } from '../types/internal-access.types'
import { Loading } from '../../../components/Loading'
import dayjs from 'dayjs'

const { Text } = Typography

interface LoginAccessDrawerProps {
  login: ZealysLogin | null
  onClose: () => void
  onGrantAccess: () => void
  onRefresh: () => void
}

export function LoginAccessDrawer({ login, onClose, onGrantAccess, onRefresh }: LoginAccessDrawerProps) {
  const { message } = App.useApp()
  const [accessList, setAccessList] = useState<LoginInternalAccess[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    if (login) {
      loadAccess()
    }
  }, [login])

  const loadAccess = async () => {
    if (!login) return

    try {
      setLoading(true)
      setError(null)
      const data = await getLoginInternalAccess(login.id)
      setAccessList(data)
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to load access'))
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (id: string) => {
    try {
      await deleteAccess(id)
      message.success('Access revoked successfully')
      loadAccess()
      onRefresh()
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to revoke access')
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', padding: '24px' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: 16,
          paddingBottom: 16,
          borderBottom: '1px solid #f0f0f0'
        }}
      >
        <Space direction="vertical" size={0}>
          <Text strong style={{ fontSize: '16px' }}>
            {login?.loginName || login?.email}
          </Text>
          <Text type="secondary" style={{ fontSize: '14px' }}>
            {login?.email}
          </Text>
        </Space>
        <Space>
          <Button type="primary" icon={<PlusOutlined />} onClick={onGrantAccess}>
            Grant Access
          </Button>
          <Button type="text" icon={<CloseOutlined />} onClick={onClose} />
        </Space>
      </div>

      <div style={{ flex: 1, overflow: 'auto' }}>
        {loading && <Loading />}

        {error && (
          <Alert
            message="Error Loading Access"
            description={error.message}
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
            action={
              <Button size="small" onClick={loadAccess}>
                Retry
              </Button>
            }
          />
        )}

        {!loading && !error && accessList.length === 0 && <Empty description="No access granted yet" />}

        {!loading && !error && accessList.length > 0 && (
          <List
            dataSource={accessList}
            renderItem={access => (
              <List.Item
                key={access.id}
                actions={[
                  <Popconfirm
                    title="Revoke Access"
                    description="Are you sure you want to revoke this access?"
                    onConfirm={() => handleDelete(access.id)}
                    okText="Yes"
                    cancelText="No"
                  >
                    <Button type="text" size="small" icon={<DeleteOutlined />} danger>
                      Revoke
                    </Button>
                  </Popconfirm>
                ]}
              >
                <List.Item.Meta
                  title={
                    <Space>
                      <Tag
                        color={
                          access.isExpired
                            ? 'default'
                            : access.role?.includes('admin')
                            ? 'purple'
                            : access.role?.includes('approver')
                            ? 'blue'
                            : access.role?.includes('requester')
                            ? 'gold'
                            : access.role?.includes('user')
                            ? 'green'
                            : 'default'
                        }
                      >
                        {formatRole(access.role)}
                      </Tag>
                      {access.isExpired && <Tag color="red">Expired</Tag>}
                    </Space>
                  }
                  description={
                    <Space direction="vertical" size={4} style={{ width: '100%' }}>
                      <div>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          Granted by: {access.grantedBy}
                        </Text>
                      </div>
                      <div>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          Granted: {dayjs(access.grantedDate).format('YYYY-MM-DD HH:mm')}
                        </Text>
                      </div>
                      {access.expiryDate && (
                        <div>
                          <Text type="secondary" style={{ fontSize: '12px' }}>
                            Expires: {dayjs(access.expiryDate).format('YYYY-MM-DD HH:mm')}
                          </Text>
                        </div>
                      )}
                      {access.notes && (
                        <div style={{ marginTop: 8 }}>
                          <Text type="secondary" style={{ fontSize: '12px', fontStyle: 'italic' }}>
                            {access.notes}
                          </Text>
                        </div>
                      )}
                    </Space>
                  }
                />
              </List.Item>
            )}
          />
        )}
      </div>
    </div>
  )
}
